import { createApi } from '@reduxjs/toolkit/query/react';
import dayjs from 'dayjs';

import { setUser } from 'store/user/userSlice';

import { DateFormat } from 'utils/enums';
import { baseQueryWithReauth } from 'utils/services';

import {
  AddProfileImageReqProps,
  AddProfileImageResProps,
  GetBillingHistoryQueryResProps,
  GetBillingHistoryReqProps,
  GetBillingHistoryResProps,
  GetCreditBalanceReqProps,
  GetCreditBalanceResProps,
  GetCreditCardInformationReqProps,
  GetCreditCardInformationResProps,
  GetExternalAccountsResProps,
  GetInvoiceReqProps,
  GetInvoiceResProps,
  GetMyAccountReqProps,
  GetMyAccountResProps,
  GetPlanRecommendationResProps,
  GetSubscriptionInfoResProps,
  MarkInitialLoginReqProps,
  MarkInitialLoginResProps,
  PayInvoiceReqProps,
  PayInvoiceResProps,
  PreviewChangeSubscriptionReqProps,
  PreviewChangeSubscriptionResProps,
  SendRecommendationDecisionReqProps,
  UpdateMyAccountReqProps,
  UpdateMyAccountResProps,
  UpdatePaymentProfileReqProps,
  UpdatePaymentProfileResProps
} from './myAccount.types';

export const myAccountApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    addProfileImage: build.mutation<AddProfileImageResProps, AddProfileImageReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/my-account/profile-image'
      })
    }),
    deletePaymentMethod: build.mutation<
      UpdateMyAccountResProps,
      { id: string; isInsuranceSubscription?: boolean }
    >({
      query: ({ id, isInsuranceSubscription }) => ({
        method: 'DELETE',
        url: `/my-account/payment-profile/${id}${isInsuranceSubscription ? '?isInsuranceSubscription=true' : ''}`
      })
    }),
    getBillingHistory: build.query<GetBillingHistoryResProps, GetBillingHistoryReqProps>({
      keepUnusedDataFor: 0,
      query: (params) => ({
        params,
        url: '/my-account/billing-history'
      }),
      transformResponse: (response: GetBillingHistoryQueryResProps) => {
        return {
          ...response,
          data: response.data?.map((el) => ({
            amount: +el.amount,
            cardBrand:
              el.payment?.paymentMethod.cardBrand ||
              (el.payment?.paymentMethod.email ? 'PayPal' : ''),
            credit: +(el.credit?.appliedAmount || '0'),
            date: dayjs(el.createdAt).format(DateFormat.MMMM_DD_YYYY),
            discountAmount: +(el.discount?.discountAmount || '0'),
            discountTitle: el.discount?.title || '',
            email: el.payment?.paymentMethod.email || '',
            invoice: el.title,
            invoiceUrl: el.invoiceUrl,
            maskedCardNumber: el.payment?.paymentMethod.maskedCardNumber || '',
            number: el.number,
            paidAmount: +el.paidAmount,
            refundAmount: +el.refundAmount,
            status: el.status.charAt(0).toUpperCase() + el.status.slice(1)
          }))
        };
      }
    }),
    getCreditBalance: build.query<GetCreditBalanceResProps, GetCreditBalanceReqProps>({
      keepUnusedDataFor: 0,
      query: () => '/my-account/account-balances'
    }),
    getCreditCardInformation: build.query<
      GetCreditCardInformationResProps,
      GetCreditCardInformationReqProps
    >({
      query: (params) => ({
        ...(!!params && { params }),
        url: '/my-account/credit-card-information'
      })
    }),
    getExternalAccounts: build.query<GetExternalAccountsResProps, { provider: string }>({
      query: ({ provider }) => `/external-accounts/${provider}`
    }),
    getInvoice: build.query<GetInvoiceResProps, GetInvoiceReqProps>({
      keepUnusedDataFor: 0,
      query: (invoiceId) => ({
        url: `/my-account/invoices/${invoiceId}`
      })
    }),
    getMyAccount: build.query<GetMyAccountResProps, GetMyAccountReqProps>({
      keepUnusedDataFor: 0,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setUser(data.data));
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(e);
        }
      },
      query: () => '/my-account'
    }),
    getPlanRecommendation: build.query<GetPlanRecommendationResProps, void>({
      query: () => '/my-account/plan-recommendation'
    }),
    getSubscriptionInfo: build.query<GetSubscriptionInfoResProps, void>({
      query: () => '/my-account/subscription-info'
    }),
    markInitialLogin: build.mutation<MarkInitialLoginResProps, MarkInitialLoginReqProps>({
      query: () => ({
        method: 'PATCH',
        url: '/my-account/mark-initial-login'
      })
    }),
    payInvoice: build.mutation<PayInvoiceResProps, PayInvoiceReqProps>({
      query: (invoiceId) => ({
        method: 'POST',
        url: `/my-account/invoices/${invoiceId}/pay`
      })
    }),
    previewChangeSubscription: build.query<
      PreviewChangeSubscriptionResProps,
      PreviewChangeSubscriptionReqProps
    >({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/my-account/preview-change-subscription'
      })
    }),
    sendRecommendationDecision: build.mutation<unknown, SendRecommendationDecisionReqProps>({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/my-account/plan-recommendation'
      })
    }),
    updateDefaultPaymentMethod: build.mutation<
      UpdateMyAccountResProps,
      { id: string; isInsuranceSubscription?: boolean }
    >({
      query: ({ id, isInsuranceSubscription }) => ({
        method: 'PUT',
        url: `/my-account/payment-profile/${id}/set-default${isInsuranceSubscription ? '?isInsuranceSubscription=true' : ''}`
      })
    }),
    updateMyAccount: build.mutation<UpdateMyAccountResProps, UpdateMyAccountReqProps>({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/my-account'
      })
    }),
    updatePaymentProfile: build.mutation<
      UpdatePaymentProfileResProps,
      UpdatePaymentProfileReqProps
    >({
      query: ({ method, isInsuranceSubscription, ...body }) => ({
        body,
        method,
        url: `/my-account/payment-profile${isInsuranceSubscription ? '?isInsuranceSubscription=true' : ''}`
      })
    })
  }),
  reducerPath: 'myAccountApi',
  tagTypes: ['MyAccount']
});

export const {
  useGetMyAccountQuery,
  useGetInvoiceQuery,
  usePayInvoiceMutation,
  useLazyGetMyAccountQuery,
  useGetBillingHistoryQuery,
  useUpdateMyAccountMutation,
  useLazyPreviewChangeSubscriptionQuery,
  useAddProfileImageMutation,
  useGetCreditCardInformationQuery,
  useLazyGetCreditCardInformationQuery,
  useUpdatePaymentProfileMutation,
  useMarkInitialLoginMutation,
  useGetCreditBalanceQuery,
  useDeletePaymentMethodMutation,
  useUpdateDefaultPaymentMethodMutation,
  useGetPlanRecommendationQuery,
  useSendRecommendationDecisionMutation,
  useGetSubscriptionInfoQuery,
  useGetExternalAccountsQuery
} = myAccountApi;
